/**
 * PaymentApp.jsx - Payment Link Checkout
 *
 * Flow:
 * 1. User visits Flask: /payment/abc-123
 * 2. Flask redirects to: /pay?session_id=abc-123
 * 3. React calls: POST /checkout/abc-123
 * 4. Flask returns: { clientSecret, stripeConnectedAccount, products, total }
 * 5. React loads Stripe (with connected account if provided)
 * 6. React renders Stripe PaymentElement with full checkout UI
 */
import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PaymentCheckoutPage from './PaymentCheckoutPage';
import PaymentComplete from './PaymentComplete';
import ErrorPreview from './ErrorPreview';
import NotFound from './NotFound';
import Landing from './Landing';
import Status from './Status';
import logger from './logger';

import './App.css';

// Context for checkout data - survives CheckoutProvider remount
const PaymentDataContext = createContext(null);

export const usePaymentData = () => {
  const context = useContext(PaymentDataContext);
  if (!context) {
    throw new Error('usePaymentData must be used within PaymentDataProvider');
  }
  return context;
};

// Helper to format currency
const formatAmount = (amountCents, currency = 'USD') => {
  if (!amountCents) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amountCents / 100);
};

const PaymentApp = () => {
  // Check if we're on a page that doesn't need session_id
  const pathname = window.location.pathname;
  const isCompletePage = pathname === '/complete';
  const isErrorPreview = pathname === '/error';
  const isRootPage = pathname === '/';
  const isStatusPage = pathname === '/status';
  const skipSessionCheck = isCompletePage || isErrorPreview || isRootPage || isStatusPage;

  // Get session_id from URL (only needed for /pay)
  const [linkSessionId] = useState(() => {
    if (skipSessionCheck) return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('session_id');
  });

  // === SESSION STATE ===
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [sessionKey, setSessionKey] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(!skipSessionCheck);
  const [isUpdatingSession, setIsUpdatingSession] = useState(false);
  const [error, setError] = useState(null);

  // Track connected account to avoid reloading Stripe unnecessarily
  const connectedAccountRef = useRef(null);

  // === CACHED DATA (from first API response) ===
  const [cachedLineItems, setCachedLineItems] = useState([]);
  const [cachedSubtotalCents, setCachedSubtotalCents] = useState(0);
  const [cachedConvenienceFeeCents, setCachedConvenienceFeeCents] = useState(0);
  const [cachedBusinessName, setCachedBusinessName] = useState('');
  const [cachedCurrency, setCachedCurrency] = useState('USD');
  const [cachedConnectedAccount, setCachedConnectedAccount] = useState(null);
  const [cachedCustomerEmail, setCachedCustomerEmail] = useState('');
  const [hasCachedData, setHasCachedData] = useState(false);

  // === TIP STATE ===
  const [selectedTipCents, setSelectedTipCents] = useState(0);

  // Create checkout session for payment link
  const createSession = useCallback(
    async (tipAmount = 0) => {
      if (!linkSessionId) {
        throw new Error('No session_id in URL');
      }

      logger.info('Creating checkout session for link:', linkSessionId, 'tip:', tipAmount);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let response;
      try {
        response = await fetch(`${backendUrl}/api/public/v1/checkout/${linkSessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tip_amount: tipAmount }),
        });
      } catch (networkError) {
        throw new Error('Unable to connect to server. Please check your connection and try again.');
      }

      if (!response.ok) {
        let errorMessage = 'Payment link not found or expired';
        try {
          const data = await response.json();
          errorMessage = data.error || errorMessage;
        } catch {
          // Response wasn't JSON, use default message
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // The fee follows the tip, so read it from every response.
      setCachedConvenienceFeeCents(data.convenienceFee || 0);

      // Load Stripe with connected account if provided (only on first call)
      if (!stripePromise || connectedAccountRef.current !== data.stripeConnectedAccount) {
        connectedAccountRef.current = data.stripeConnectedAccount;
        setCachedConnectedAccount(data.stripeConnectedAccount);
        const stripeOptions = data.stripeConnectedAccount
          ? { stripeAccount: data.stripeConnectedAccount }
          : undefined;
        setStripePromise(loadStripe(process.env.REACT_APP_STRIPE_KEY, stripeOptions));
        logger.info(
          'Loaded Stripe',
          data.stripeConnectedAccount
            ? `for connected account: ${data.stripeConnectedAccount}`
            : 'for platform account'
        );
      }

      // Cache products on first call
      if (!hasCachedData && data.products) {
        const lineItems = data.products.map((p, index) => ({
          id: `line-${index}`,
          name: p.product_name,
          description: '',
          quantity: p.quantity || 1,
          unitAmountCents: p.unit_amount,
          lineTotalCents: p.unit_amount * (p.quantity || 1),
        }));

        // Use productsTotal from backend (excludes convenience fee and tip)
        const subtotal =
          data.productsTotal || lineItems.reduce((sum, item) => sum + item.lineTotalCents, 0);

        setCachedLineItems(lineItems);
        setCachedSubtotalCents(subtotal);
        setCachedBusinessName(data.businessName || 'Payment');
        setCachedCurrency('USD');
        setCachedCustomerEmail(data.customerEmail || '');
        setHasCachedData(true);

        logger.info('Cached payment link data:', {
          items: lineItems.length,
          subtotal,
          convenienceFee: data.convenienceFee,
        });
      }

      return data.clientSecret;
    },
    [linkSessionId, hasCachedData, stripePromise]
  );

  // Handle tip change
  const handleTipChange = useCallback(
    async (newTipCents) => {
      logger.info('Tip changed to:', newTipCents);

      setSelectedTipCents(newTipCents);

      setIsUpdatingSession(true);
      try {
        const newSecret = await createSession(newTipCents);
        setClientSecret(newSecret);
        setSessionKey((prev) => prev + 1);
      } catch (err) {
        logger.error('Failed to update session:', err);
      } finally {
        setIsUpdatingSession(false);
      }
    },
    [createSession]
  );

  // Dummy cacheCheckoutData for compatibility (data already cached from API)
  const cacheCheckoutData = useCallback(() => {}, []);

  // Initial session creation (skip for pages that don't need it)
  useEffect(() => {
    if (skipSessionCheck) return;

    const init = async () => {
      if (!linkSessionId) {
        setError('This payment link is invalid or has expired.');
        setIsInitialLoading(false);
        return;
      }

      try {
        const secret = await createSession(selectedTipCents);
        setClientSecret(secret);
        setIsInitialLoading(false);
      } catch (err) {
        setError(err.message);
        setIsInitialLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedSubtotal = React.useMemo(
    () => formatAmount(cachedSubtotalCents, cachedCurrency),
    [cachedSubtotalCents, cachedCurrency]
  );

  const formattedTotal = React.useMemo(
    () =>
      formatAmount(
        cachedSubtotalCents + cachedConvenienceFeeCents + selectedTipCents,
        cachedCurrency
      ),
    [cachedSubtotalCents, cachedConvenienceFeeCents, selectedTipCents, cachedCurrency]
  );

  const formattedConvenienceFee = React.useMemo(
    () => formatAmount(cachedConvenienceFeeCents, cachedCurrency),
    [cachedConvenienceFeeCents, cachedCurrency]
  );

  const contextValue = React.useMemo(
    () => ({
      cachedLineItems,
      cachedSubtotalCents,
      cachedConvenienceFeeCents,
      cachedBusinessName,
      cachedCurrency,
      cachedConnectedAccount,
      cachedCustomerEmail,
      hasCachedData,
      cacheCheckoutData,
      selectedTipCents,
      handleTipChange,
      formattedTotal,
      formattedSubtotal,
      formattedConvenienceFee,
      isUpdatingSession,
    }),
    [
      cachedLineItems,
      cachedSubtotalCents,
      cachedConvenienceFeeCents,
      cachedBusinessName,
      cachedCurrency,
      cachedConnectedAccount,
      cachedCustomerEmail,
      hasCachedData,
      cacheCheckoutData,
      selectedTipCents,
      handleTipChange,
      formattedTotal,
      formattedSubtotal,
      formattedConvenienceFee,
      isUpdatingSession,
    ]
  );

  const appearance = {
    theme: 'flat',
    variables: { colorPrimaryText: '#262626' },
  };

  // For /complete and /error pages, render directly without loading state
  if (skipSessionCheck) {
    return (
      <div className="App">
        <Router>
          <Routes>
            <Route path="/complete" element={<PaymentComplete />} />
            <Route path="/error" element={<ErrorPreview />} />
            <Route path="/" element={<Landing />} />
            <Route path="/status" element={<Status />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <NotFound message={error} />
      </div>
    );
  }

  if (isInitialLoading || !clientSecret || !stripePromise) {
    return (
      <div className="App">
        <div className="checkout-loading">
          <div className="checkout-loading-spinner"></div>
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <PaymentDataContext.Provider value={contextValue}>
        <Router>
          <Routes>
            <Route
              path="/pay"
              element={
                <PaymentCheckoutPage
                  stripePromise={stripePromise}
                  clientSecret={clientSecret}
                  sessionKey={sessionKey}
                  appearance={appearance}
                />
              }
            />
            <Route path="/complete" element={<PaymentComplete />} />
            <Route path="/error" element={<ErrorPreview />} />
          </Routes>
        </Router>
      </PaymentDataContext.Provider>
    </div>
  );
};

export default PaymentApp;
